import { Injectable } from '@angular/core'
import { formatDate } from '@angular/common'
import {
  AbstractControl,
  FormGroup,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms'
import { RegexEnum } from '../enums/regex.enum'
import { PaginationModel } from '../models/pagination.model'
import { SortingModel } from '../models/sorting.model'
import { Router } from '@angular/router'
import { IPFSReport } from './filter-storage.service'

@Injectable({
  providedIn: 'root',
})
export class HelperService {
  public static dateToString(
    date: Date = null,
    format: string = 'yyyy-MM-dd',
    locale: string = 'en',
    timezone?: string
  ): string {
    if (date) {
      return formatDate(date, format, locale, timezone)
    }
    return ''
  }

  public static isNullOrUndefined(value: any): boolean {
    return value === null || value === undefined
  }

  public static isNullUndefEmptyStr(value: any): boolean {
    return value === null || value === undefined || value === ''
  }

  public static isNullOrEmptyStr(value: any): boolean {
    return value === null || value === ''
  }

  public static deepCopy(object: any) {
    return JSON.parse(JSON.stringify(object))
  }

  public static inArrayByCriteria(items: any[], by: string, value: any): boolean {
    if (items.length) {
      for (const i in items) {
        if (items[i] && items[i][by] && items[i][by] === value) {
          return true
        }
      }
    }
    return false
  }

  public static inArray(items: any[], value: any, insensitive: boolean = false): boolean {
    if (items.length) {
      if (insensitive) {
        for (const i in items) {
          if (
            !HelperService.isNullOrUndefined(items[i]) &&
            items[i].toLowerCase() === value.toLowerCase()
          ) {
            return true
          }
        }
      } else {
        for (const i in items) {
          if (!HelperService.isNullOrUndefined(items[i]) && items[i] === value) {
            return true
          }
        }
      }
    }
    return false
  }

  public static arrayIntersect(arr1: any[], arr2: any[], by: string): any[] | null {
    if (arr1.length && arr2.length) {
      const intersect = []
      for (const i in arr1) {
        if (arr1[i] && HelperService.inArrayByCriteria(arr2, by, arr1[i])) {
          intersect.push(arr1[i])
        }
      }
      return intersect.length > 0 ? intersect : null
    }
    return null
  }

  public static complexObjectToQuery(obj: any, prefix: string = null): string {
    if (obj && Object.keys(obj).length) {
      const fields: any[] = []
      for (const i in obj) {
        if (obj.hasOwnProperty(i)) {
          if (typeof obj[i] === 'object') {
            const query: string = HelperService.complexObjectToQuery(
              obj[i],
              prefix ? prefix + '[' + i + ']' : i,
            )
            if (query !== '') {
              fields.push(query)
            }
          } else {
            if (obj[i] !== null) {
              fields.push(
                (prefix ? prefix + '[' + i + ']' : i) +
                  '=' +
                  (typeof obj[i] === 'undefined' ? '' : encodeURIComponent(obj[i])),
              )
            }
          }
        }
      }
      if (fields.length > 0) {
        return fields.join('&')
      }
    }
    return ''
  }

  public static isEmptyObjectByValues(object: any): boolean {
    if (object && Object.keys(object).length) {
      for (const i in object) {
        if (object.hasOwnProperty(i)) {
          if (object[i] !== undefined && object[i] !== null) {
            return false
          }
        }
      }
    }
    return true
  }

  public static findObjectByAttributeValue(
    objects: any[],
    attribute: string,
    value: string | number | null,
  ): any {
    for (let object of objects) {
      if (attribute in object && object[attribute] === value) {
        return object
      }
    }
    return null
  }

  public static getColorClassForBullet(value: any): string | null {
    const newVal: number = parseFloat(value)
    if (newVal >= 0 && newVal < 3) {
      return 'circle-red'
    } else if (newVal >= 3 && newVal < 6) {
      return 'circle-orange'
    } else if (newVal >= 6 && newVal < 9) {
      return 'circle-yellow'
    } else if (newVal >= 9 && newVal < 11) {
      return 'circle-green'
    } else if (newVal >= 11) {
      return 'circle-gray'
    }
    return null
  }

  public static getColorBulletHTML(value: any, defaultValue: string = '-'): string {
    if (!HelperService.isNullOrUndefined(value)) {
      const cssClass: string | null = this.getColorClassForBullet(value)
      if (cssClass) {
        return '<span class="circle ' + cssClass + '">' + value + '</span>'
      }
      return '<span class="circle">' + value + '</span>'
    }
    return defaultValue
  }

  public static getObjectWithoutNullValues(object: any, skip: string[] = []): any {
    const cleanObject: any = {}
    if (object && Object.keys(object).length) {
      for (const i in object) {
        if (object.hasOwnProperty(i)) {
          if (object[i] !== undefined && object[i] !== null && skip.indexOf(i) === -1) {
            cleanObject[i] = object[i]
          }
        }
      }
    }
    return cleanObject
  }

  public static getDeviationBulletHTML(value: any): string {
    if (!HelperService.isNullOrUndefined(value)) {
      const fill = parseInt(value, 0) === 2 ? 'X' : '.'
      return (
        '<span class="circle small-circle ' +
        this.getColorClassForDeviation(value) +
        '">' +
        fill +
        '</span>'
      )
    }
    return '-'
  }

  public static getColorClassForDeviation(value: any): string {
    const newVal = parseInt(value, 0)
    if (newVal === 1) {
      return 'circle-red-outline'
    } else if (newVal === 2) {
      return 'circle-red'
    } else if (newVal === 3) {
      return 'circle-green circle-green-text'
    } else if (newVal === 4) {
      return 'circle-yellow circle-yellow-text'
    }
    return ''
  }

  public static escapeRegExp(strToEscape) {
    // Escape special characters for use in a regular expression
    return strToEscape.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&')
  }

  public static trim(origString, charToTrim) {
    charToTrim = this.escapeRegExp(charToTrim)
    const regEx = new RegExp('^[' + charToTrim + ']+|[' + charToTrim + ']+$', 'g')
    return origString.replace(regEx, '')
  }

  public static msToMinSecMilliFormat(ms: number, withMilli: boolean = true): string {
    const min = Math.floor(ms / (60 * 1000))
    const sec = Math.floor((ms - min * (60 * 1000)) / 1000)
    if (withMilli) {
      const milli = ms - (min * (60 * 1000) + sec * 1000)
      return `${min < 10 ? '0' + min : min}:${sec < 10 ? '0' + sec : sec},${milli}`
    }
    return `${min < 10 ? '0' + min : min}:${sec < 10 ? '0' + sec : sec}`
  }

  public static secToHMSFormat(sec: number): string {
    const hours = Math.floor(sec / 3600)
    sec %= 3600
    const min = Math.floor(sec / 60)
    const seconds = Math.floor(sec % 60)
    return [
      hours < 10 ? '0' + hours : hours,
      min < 10 ? '0' + min : min,
      seconds < 10 ? '0' + seconds : seconds,
    ].join(':')
  }

  public static secToDHMSFormat(sec: number): string {
    const days = Math.floor(sec / (24 * 3600))
    sec %= 24 * 3600
    if (days) {
      return `${days}d ${this.secToHMSFormat(sec)}`
    }
    return this.secToHMSFormat(sec)
  }

  public static getFileExtension(file: File): string {
    const items = file.name.split('.')
    const extension = items[items.length - 1].toLowerCase()

    return `.${extension}`
  }

  public static copyMessage(val: string) {
    const selBox: HTMLTextAreaElement = document.createElement('textarea')
    selBox.style.position = 'fixed'
    selBox.style.left = '0'
    selBox.style.top = '0'
    selBox.style.opacity = '0'
    selBox.value = val
    document.body.appendChild(selBox)
    selBox.focus()
    selBox.select()
    document.execCommand('copy')
    document.body.removeChild(selBox)
  }

  public static getLastTestValidationIco(status: string): string | null {
    let icoName = ''
    switch (true) {
      case status === 'VALID':
        icoName = 'valid'
        break
      case status === 'OVERDUE':
        icoName = 'overdue'
        break
      case status === 'FAILED':
        icoName = 'failed'
        break
      case status === 'TEST REQUIRED':
        icoName = 'test_required'
        break
      case status === 'TEST REQ.':
        icoName = 'test_req'
        break
      default:
        return null
    }
    return `assets/images/icons/proof-validation/${icoName}.svg`
  }

  public static getBodyTableHeight(heightToReduce: number, minHeight: number = 560): number {
    const pageHeight = window.innerHeight
    const tableHeight = pageHeight - heightToReduce
    return tableHeight < minHeight ? minHeight : tableHeight
  }

  public static scrollToTopNzTable(index: number = 0): void {
    const element = document.getElementsByClassName('ant-table-body')[index]
    if (element) {
      element.scrollTop = 0
    }
  }

  public static scrollToBottomNzTable(parentId: string = null, index: number = 0): void {
    let element = null
    if (parentId) {
      const parentElement = document.getElementById(parentId)
      element = parentElement.getElementsByClassName('ant-table-body')[index]
    } else {
      element = document.getElementsByClassName('ant-table-body')[index]
    }
    setTimeout(() => element.scroll({ top: element.scrollHeight, behavior: 'smooth' }), 100)
  }

  public static numberWithSpaces(x: string | number): string {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  }

  public static copyOnlyKeysFromObject(obj: any, keys: string[]): any {
    const copied: any = {}
    const objectKeys: string[] = Object.keys(obj)
    for (const i in objectKeys) {
      if (keys.indexOf(objectKeys[i]) !== -1) {
        copied[objectKeys[i]] = obj[objectKeys[i]]
      }
    }
    return copied
  }

  public static clickOnRcaFaultUrl(
    tagName: string,
    rcaFaultNo?: string,
    target: string = '_blank',
    history: boolean = false,
  ): void {
    if (!rcaFaultNo || rcaFaultNo.length === 0) {
      return
    }
    let url: string
    if (new RegExp(RegexEnum.REGEX_URL).test(rcaFaultNo)) {
      url = rcaFaultNo
    } else {
      url = `/#/spas/fault-analysis/root-cause-analysis/${encodeURIComponent(
        tagName,
      )}/${encodeURIComponent(rcaFaultNo)}`
      if (history) {
        url = url + '?history=1'
      }
    }
    this.clickOnUrl(url, target)
  }

  public static clickOnRcaDemandUrl(
    tagName: string,
    rcaDemandNo?: string,
    target: string = '_blank',
    history: boolean = false,
  ): void {
    if (!rcaDemandNo || rcaDemandNo.length === 0) {
      return
    }
    let url: string
    if (new RegExp(RegexEnum.REGEX_URL).test(rcaDemandNo)) {
      url = rcaDemandNo
    } else {
      url = `/#/spas/initiators/demand-analysis/${encodeURIComponent(tagName)}/${encodeURIComponent(
        rcaDemandNo,
      )}`
      if (history) {
        url = url + '?history=1'
      }
    }
    this.clickOnUrl(url, target)
  }

  public static clickOnRcaOverrideUrl(
    tagName: string,
    rcaOverrideNo?: string,
    target: string = '_blank',
    history: boolean = false,
  ): void {
    if (!rcaOverrideNo || rcaOverrideNo.length === 0) {
      return
    }
    let url: string
    if (new RegExp(RegexEnum.REGEX_URL).test(rcaOverrideNo)) {
      url = rcaOverrideNo
    } else {
      url = `/#/spas/overrides/override-analysis/${encodeURIComponent(
        tagName,
      )}/${encodeURIComponent(rcaOverrideNo)}`
      if (history) {
        url = url + '?history=1'
      }
    }
    this.clickOnUrl(url, target)
  }

  public static clickOnUrl(url?: string, target: string = '_blank'): void {
    if (!url || url.length === 0) {
      return
    }
    const clickable: HTMLAnchorElement = document.createElement('a')
    clickable.href = url
    clickable.target = target
    clickable.click()
    clickable.remove()
  }

  public static updateDeviationOnObject(
    items: any[],
    keys: { mo: string; deviation: string; url: string },
    data: { mainObject: string; active?: boolean; url?: string },
  ): void {
    for (const item of items) {
      if (item[keys.mo] === data.mainObject) {
        if (Boolean(item[keys.deviation]) !== data.active) {
          item[keys.deviation] = data.active ? 1 : 0
        }
        if (item[keys.url] !== data.url) {
          item[keys.url] = data.url
        }
      }
    }
  }

  public static getFormErrors(form: AbstractControl): any {
    if (form instanceof UntypedFormControl) {
      // Return FormControl errors or null
      return form.errors ?? null
    }
    if (form instanceof UntypedFormGroup) {
      const groupErrors = form.errors
      // Form group can contain errors itself, in that case add'em
      const formErrors = groupErrors ? { groupErrors } : {}
      Object.keys(form.controls).forEach(key => {
        // Recursive call of the FormGroup fields
        const error = this.getFormErrors(form.get(key))
        if (error !== null) {
          // Only add error if not null
          formErrors[key] = error
        }
      })
      // Return FormGroup errors or null
      return Object.keys(formErrors).length > 0 ? formErrors : null
    }
  }

  public static getYearlyTimeFrame(no: number): Date[] {
    const date: Date = new Date()
    const year: number = date.getFullYear()
    const month: string =
      date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : `${date.getMonth() + 1}`
    const day: string = date.getDate() < 10 ? `0${date.getDate()}` : `${date.getDate()}`
    const prev: Date = new Date(Date.parse(`${year}-${month}-${day} 23:59:59`))
    const current: Date = new Date(Date.parse(`${year}-${month}-${day} 23:59:59`))
    prev.setFullYear(prev.getFullYear() - no)

    return [prev, current]
  }

  public static ucFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  public static toEmptyStringWhenBull(value: any): any {
    if (this.isNullOrUndefined(value)) {
      return ''
    }
    return value
  }

  public static fullMonthIntervalFromDate(
    date: string,
    separator: string = '.',
  ): { start: Date; end: Date } | null {
    const my: string[] = date.split(separator)
    if (my.length === 2) {
      const month: number = parseInt(my[0], 0)
      const year: number = parseInt(my[1], 0)
      const monthS: string = month < 10 ? `0${month}` : `${month}`
      const startDate: Date = new Date(Date.parse(`${year}-${monthS}-01 00:00:00`))
      const endDate: Date = new Date(JSON.parse(JSON.stringify(startDate)))
      endDate.setMonth(endDate.getMonth() + 1)
      endDate.setSeconds(endDate.getSeconds() - 1)

      return {
        start: startDate,
        end: endDate,
      }
    }
    return null
  }

  public static getMonthYearFromDate(
    date: string,
    separator: string = '.',
  ): { month: number; year: number } | null {
    const my: string[] = date.split(separator)
    if (my.length === 2) {
      return {
        month: parseInt(my[0], 0),
        year: parseInt(my[1], 0),
      }
    }
    return null
  }

  public static getNoOfDaysFromMonthOfYear(year, month): number {
    return new Date(year, month, 0).getDate()
  }

  public static getRandomInt(max: number): number {
    return Math.floor(Math.random() * max)
  }

  public static getRandomIntBetween(min, max) {
    min = Math.ceil(min)
    max = Math.floor(max)
    return Math.floor(Math.random() * (max - min + 1)) + min
  }

  public static getNoOfDaysBetween2Dates(date1: Date, date2: Date): number {
    const difference: number = date1.getTime() - date2.getTime()
    return Math.ceil(difference / (1000 * 3600 * 24))
  }

  public static patchFormControl(
    form: FormGroup<any> | UntypedFormGroup,
    name: string,
    val: any,
  ): void {
    let toPatch: any = {}
    toPatch[name] = val
    form.patchValue(toPatch)
    form.markAsDirty()
  }

  public static refreshPage(
    params: {
      filter?: any
      calendar?: any
      pagination?: PaginationModel
      sort?: SortingModel
      sid?: any,
      sda?: any
    },
    url: string,
    router: Router,
    forceRefresh: boolean = false,
  ): Promise<boolean> {
    const _params: { filter?: any, sid?: any, sda?: any, cal?: any, pag?: any, sort?: any } = {}

    if (!HelperService.isNullOrUndefined(params.filter)) {
      _params['filter'] = JSON.stringify(HelperService.getObjectWithoutNullValues(params.filter))

      if (!HelperService.isNullOrUndefined(params.sid) && params.sid > 0) {
        _params['sid'] = params.sid
      }

      if (!HelperService.isNullOrUndefined(params.sda) && params.sda == 1) {
        _params['sda'] = params.sda
      }
    }

    if (!HelperService.isNullOrUndefined(params.calendar)) {
      _params['cal'] = JSON.stringify(HelperService.getObjectWithoutNullValues(params.calendar))
    }

    if (
      !HelperService.isNullOrUndefined(params.pagination) &&
      (params.pagination.no > 1 || params.pagination.size > 1)
    ) {
      _params['pag'] = JSON.stringify(params.pagination)
    }

    if (!HelperService.isNullOrUndefined(params.sort)) {
      _params['sort'] = JSON.stringify(params.sort)
    }

    if (forceRefresh) {
      _params['t'] = new Date().getTime()
    }

    return router.navigate([url], { queryParams: _params })
  }

  public static getFilterToSave(params: {
    filter?: any
    calendar?: any
    pagination?: PaginationModel
    sort?: SortingModel
    other?: any
  }): IPFSReport {
    const _filters: IPFSReport = { f: null, c: null, p: null, s: null, o: null }

    if (
      !HelperService.isNullOrUndefined(params.filter) &&
      !HelperService.isEmptyObjectByValues(params.filter)
    ) {
      _filters.f = params.filter
    }

    if (
      !HelperService.isNullOrUndefined(params.calendar) &&
      !HelperService.isEmptyObjectByValues(params.calendar)
    ) {
      _filters.c = params.calendar
    }

    if (
      !HelperService.isNullOrUndefined(params.pagination) &&
      (params.pagination.no > 1 || params.pagination.size > 25)
    ) {
      _filters.p = params.pagination
    }

    if (!HelperService.isNullOrUndefined(params.sort)) {
      _filters.s = params.sort
    }

    if (
      !HelperService.isNullOrUndefined(params.other) &&
      !HelperService.isEmptyObjectByValues(params.other)
    ) {
      _filters.o = params.other
    }

    return HelperService.getObjectWithoutNullValues(_filters)
  }

  public static getNoOfDaysBetweenDates(
    startDate?: string | number | Date,
    endDate?: string | number | Date
  ): number|null {

    if (!startDate || !endDate) {
      return null
    }

    const format: string = 'MM/dd/yyyy HH:mm:ss'
    const d1: Date = new Date(formatDate(startDate, format, 'en'))
    const d2: Date = new Date(formatDate(endDate, format, 'en'))
    return Math.ceil((d2.getTime() - d1.getTime()) / (1000 * 3600 * 24))
  }

  public static windowOpen(
    url: string,
    urlPrefix: string = '#',
    target: '_blank' | '_self' | '_parent' | null = null
  ): void {
    window.open(urlPrefix + url, target)
    return
  }
}
